// Settings › General › Consent text (design/screens/settings-general.html "Consent text"; UC-GOVERNANCE-008; D47, R-15): the
// current consent text version with its effective date and publisher, the version it superseded, the opening paragraph, and
// "Publish new version" — the whole text and an effective date, sent under the Control PIN (`PrivacyPublish`) with one
// Idempotency-Key per distinct body. Listing and publishing are the Owner's (`tenancy.privacy.write`, the route inventory's
// gate on both); anyone else is told who manages it. The privacy notice is published through the same route (UC-GOVERNANCE-008
// A1) and has no panel on the prototype.
import { PRIVACY_BODY_MAX_LENGTH, controlIdDisplay, type PrivacyDocumentPublish, type PrivacyDocumentView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { privacyDocuments } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { formatDate, localDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Chip, Empty, ErrorAlert, Field, KV, Loading, LockMark, Modal, Panel, TextArea, TextInput } from "../ui/bits";

/** `1 Sept 2026` for a calendar date — the date itself, never shifted by a timezone. */
const day = (date: string): string => new Intl.DateTimeFormat("en-ZA", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00.000Z`));
const openingParagraph = (body: string): string => body.trim().split(/\n\s*\n/)[0]!.trim();

export function ConsentTextPanel({ suspended }: { suspended: boolean }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const owner = can(s, "tenancy.privacy.write");
  const q = useQuery({ queryKey: ["privacy-documents", "ConsentText"], queryFn: () => privacyDocuments.list({ docType: "ConsentText" }), enabled: owner });
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const versions = q.data?.data.items ?? [];
  const current = versions.find((v) => v.isCurrent) ?? null;
  const previous = current ? versions.find((v) => v.version === current.version - 1) ?? null : null;
  const blocked = block ? WRITE_BLOCK_TITLE[block] : suspended ? "The organisation is suspended; nothing can be published" : null;
  const meta = "POPIA · shown on the kiosk and in the desk capture";
  if (!owner) {
    return (
      <Panel title="Consent text" meta={meta}>
        <span className="help">The Owner publishes the consent text and its versions (Control PIN). The purposes and the practice's wording for each are under <Link to="/settings/consent">Settings › Consent</Link>, also the Owner's.</span>
      </Panel>
    );
  }
  return (
    <Panel title="Consent text" meta={meta} head={current && <Chip tone="ok" dot className="right">v{current.version} current</Chip>} foot={<>
      <button className="btn" type="button" disabled={!!blocked || q.isPending} title={blocked ?? "Requires a Control PIN"} onClick={() => { setNotice(null); setPublishing(true); }}>Publish new version<LockMark /></button>
      <span className="help right">A new version asks every patient to sign again when they next check in.</span>
    </>}>
      {notice}
      {q.isPending && <Loading what="consent text" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {q.data && !current && <Empty title="No consent text published yet.">Publish the first version; the kiosk and the desk capture show it from its effective date.</Empty>}
      {current && (
        <>
          <KV rows={[
            ["Current version", <>v{current.version} · in force from {day(current.effectiveFrom)}{current.publishedAt ? ` · published ${formatDate(current.publishedAt)}` : ""}{current.publishedBy ? <> · {current.publishedBy.displayName} <span className="mono">{controlIdDisplay(current.publishedBy.controlId)}</span></> : null}</>],
            ...(previous ? [["Previous version", `v${previous.version} · in force from ${day(previous.effectiveFrom)} · superseded ${day(current.effectiveFrom)}`] as [ReactNode, ReactNode]] : []),
            ["Purposes", <>6, in a fixed order · marketing off by default · <Link to="/settings/consent">managed under Consent</Link></>],
            ["Re-consent", <>{current.version > 1 ? `Patients who signed an earlier version are asked to sign v${current.version} when they next check in` : "Patients without a consent are asked to sign when they next check in"} · <Link to="/patients/re-consent">who still has to</Link></>],
          ]} />
          <div className="field">
            <label>Consent text v{current.version} · opening paragraph</label>
            <div className="input ta consent-text">{openingParagraph(current.bodyText)}</div>
          </div>
        </>
      )}
      {publishing && <PublishModal current={current} onClose={() => setPublishing(false)} onDone={(v) => {
        setPublishing(false);
        setNotice(<Alert tone="ok"><b>Consent text v{v.version} published.</b> {v.version > 1 ? `Consents signed on v${v.version - 1} or earlier now read as outdated; those patients are asked to re-consent at their next visit. ` : ""}Recorded in the Control Log.</Alert>);
        void qc.invalidateQueries({ queryKey: ["privacy-documents"] });
      }} />}
    </Panel>
  );
}

/**
 * UC-GOVERNANCE-008 steps 2–5: the whole text (the current one to start from) and the effective date, today by default and
 * never before the current version's; the Control PIN prompt follows the first refusal. One key per distinct body, so a lost
 * answer is replayed and a corrected body is a new request (A3, E4).
 */
function PublishModal({ current, onClose, onDone }: { current: PrivacyDocumentView | null; onClose: () => void; onDone: (v: PrivacyDocumentView) => void }) {
  const withStepUp = useStepUp();
  const today = localDate(new Date());
  const [text, setText] = useState(current?.bodyText ?? "");
  const [effectiveFrom, setEffectiveFrom] = useState(current && current.effectiveFrom > today ? current.effectiveFrom : today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const next = (current?.version ?? 0) + 1;
  const body: PrivacyDocumentPublish = { docType: "ConsentText", bodyText: text.trim(), effectiveFrom: effectiveFrom as PrivacyDocumentPublish["effectiveFrom"] };
  const json = JSON.stringify(body);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = useMemo(() => newIdempotencyKey(), [json]);
  useEffect(() => { setError(null); }, [json]);
  const local = {
    bodyText: body.bodyText === "" ? "The consent text is required." : body.bodyText === current?.bodyText.trim() ? "Change the text before publishing a new version." : undefined,
    effectiveFrom: !/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) ? "Choose the date it takes effect." : current && effectiveFrom < current.effectiveFrom ? `Not before v${current.version}'s ${day(current.effectiveFrom)}.` : undefined,
  };
  const fieldErr = (path: "bodyText" | "effectiveFrom") => local[path] ?? (isApiError(error) ? error.field(path)?.message : undefined);
  const shownOnFields = isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 && error.errors.every((f) => f.path === "bodyText" || f.path === "effectiveFrom");
  const ready = !busy && local.bodyText === undefined && local.effectiveFrom === undefined;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => privacyDocuments.publish(body, { ...g, idempotencyKey: key }), {
        title: `Publish consent text v${next}`, reason: "none",
        description: `${current ? `Every consent signed on v${current.version} or earlier will read as outdated, and those patients are asked to re-consent at their next visit. ` : ""}The publish is written to the Control Log.`,
      });
      if (r) onDone(r.data);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Publish consent text v${next}`} meta={current ? `replaces v${current.version} · earlier versions stay readable` : "the first version"} wide onClose={() => { if (!busy) onClose(); }} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!ready}>{busy ? "Publishing…" : "Publish"}<LockMark /></button>
    </>}>
      {error !== null && !shownOnFields && <Alert tone={isApiError(error) && error.code === "VERSION_CONFLICT" ? "warn" : "crit"}>{isApiError(error) && error.code === "VERSION_CONFLICT" ? <><b>Someone published meanwhile.</b> Close this, check the current version and publish again.</> : <><b>Not published.</b> {describeError(error)}</>}</Alert>}
      <Field label="Consent text" required htmlFor="consent-text-body" error={fieldErr("bodyText")} help={`The whole text patients read on the kiosk and at the desk; up to ${PRIVACY_BODY_MAX_LENGTH.toLocaleString("en-ZA")} characters. A blank line starts a new paragraph.`}>
        <TextArea id="consent-text-body" value={text} rows={12} maxLength={PRIVACY_BODY_MAX_LENGTH} autoFocus onChange={(e) => setText(e.target.value)} />
      </Field>
      <Field label="Effective from" required htmlFor="consent-text-from" error={fieldErr("effectiveFrom")} help="Today by default; consents are measured against the version in force.">
        <TextInput id="consent-text-from" type="date" value={effectiveFrom} min={current?.effectiveFrom} mono onChange={(e) => setEffectiveFrom(e.target.value)} error={!!fieldErr("effectiveFrom")} />
      </Field>
    </Modal>
  );
}
